import type { FC, ReactNode } from 'react'
import type { NumberFormat } from '@/lib/format'

import { useCallback, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { formatValue } from '@/lib/format'

interface AnimatedNumberProps {
    value: number
    format?: NumberFormat
    decimals?: number
    prefix?: string
    suffix?: string
    duration?: number
    className?: string
}

const isVisible = (element: Element): boolean => {
    const rect = element.getBoundingClientRect()

    return rect.top < window.innerHeight && rect.bottom > 0
}

/**
 * Renders the final value on the server (so the number is always correct
 * without JavaScript), then counts up from zero when it scrolls into view.
 * When the value changes while visible, it tweens from the old value.
 */
export const AnimatedNumber: FC<AnimatedNumberProps> = ({
    value,
    format = 'number',
    decimals,
    prefix = '',
    suffix = '',
    duration = 1600,
    className
}): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)
    const previous = useRef<number | null>(null)

    const render = useCallback((current: number): string => {
        return `${prefix}${formatValue(current, format, decimals)}${suffix}`
    }, [prefix, suffix, format, decimals])

    useEffect(() => {
        const element = ref.current

        if (!element) return

        const write = (text: string) => {
            const node = element.firstChild

            if (node && node.nodeType === Node.TEXT_NODE) node.nodeValue = text
            else element.textContent = text
        }

        const from = previous.current
        previous.current = value

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        let frame = 0

        const run = (start: number, tweenDuration: number) => {
            const startedAt = performance.now()

            const tick = (now: number) => {
                const progress = Math.min(1, (now - startedAt) / tweenDuration)
                const eased = 1 - Math.pow(1 - progress, 4)

                write(render(start + (value - start) * eased))

                if (progress < 1) frame = requestAnimationFrame(tick)
            }

            frame = requestAnimationFrame(tick)
        }

        if (from !== null) {
            if (from !== value && isVisible(element)) run(from, 700)
            else write(render(value))

            return () => cancelAnimationFrame(frame)
        }

        if (isVisible(element)) return

        write(render(0))

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return

            observer.disconnect()
            run(0, duration)
        }, { rootMargin: '0px 0px -8% 0px' })

        observer.observe(element)

        return () => {
            observer.disconnect()
            cancelAnimationFrame(frame)
            write(render(value))
        }
    }, [value, render, duration])

    return (
        <span ref={ref} className={cn('tabular-nums', className)}>
            {render(value)}
        </span>
    )
}
